import React, { useEffect, useMemo, useState } from "react";
import { Modal, Upload, UploadProps, UploadFile, DatePicker } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import dayjs from "dayjs";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { PaginationType } from "../../../types/common-type";
import webservice_url, {
  apibaseurl,
} from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import handleClearError from "../../../core/methods/handleClearError";
import {
  parseDate,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import { formattedDate } from "../../../core/methods/formattedDate";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import { PageBreadcrumb } from "../../../core/common/BreadCrumbForm";
import Loading from "../../../core/common/custom-loading/Loading";
import Datatable from "../../../core/common/dataTable";
import PdfIcon from "../../../style/icon/svg/PdfIcon";
import ErrorMessage from "../../../core/common/ErrorMessage";
import Footer from "../../../core/common/Footer";
import AddressDetailsView from "../hr-action/AddressDetailsView";

interface DocumentItem {
  document_id: string;
  file_name: string;
  original_name: string;
  file_path: string;
  mime_type: string;
  uploaded_at: string;
}

interface Artifact {
  case_artifact_id: string;
  assignment_template_id: string;
  assignment_id: string;
  case_id: number;
  remarks: string;
  followup_date: string;
  created_by: string;
  created_at: string;
  documents: DocumentItem[];
  id: string;
}
interface ArtifactsApiResponse {
  success: boolean;
  message?: string;
  data: {
    status: string;
    rows: Artifact[];
  };
  pagination?: {
    totalRecords: number;
  };
}

// One form entry (single check = 1 entry, multiple check = many entries)
interface FormEntry {
  entry_index: number;
  fields: any[];
}

// breadcrumb data
const breadcrumbItem = [{ label: "Case Details" }, { label: "Add Artifacts" }];

const initialUploadData = {
  remarks: "",
  followup_date: null,
  is_main_report: true,
  documents: [] as File[],
};

// Field types that are shown as read-only inputs
const VISIBLE_FIELD_TYPES = ["text", "number", "dropdown", "date", "email"];

/**
 * API can return the form in two shapes:
 *  - data.fields  -> single entry
 *  - data.entries -> [{ entry_index, fields: [] }, ...] (single or multiple)
 * This normalises both into an array of entries.
 */
const getFormEntries = (data: any): FormEntry[] => {
  if (!data) return [];

  if (Array.isArray(data.entries) && data.entries.length > 0) {
    return data.entries
      .filter((entry: any) => Array.isArray(entry?.fields))
      .map((entry: any, index: number) => ({
        entry_index: entry.entry_index ?? index,
        fields: entry.fields,
      }));
  }

  if (Array.isArray(data.fields) && data.fields.length > 0) {
    return [{ entry_index: 0, fields: data.fields }];
  }

  return [];
};

/** Dropdown -> option label, date -> formatted date, everything else -> value */
const getFieldDisplayValue = (field: any): string => {
  const raw = field?.value;

  if (raw === null || raw === undefined || raw === "") return "";

  if (field?.field_type === "dropdown" && Array.isArray(field?.options)) {
    const match = field.options.find(
      (opt: any) => String(opt?.value) === String(raw),
    );
    if (match?.label) return String(match.label);
  }

  if (field?.field_type === "date") {
    const parsed = dayjs(raw);
    if (parsed.isValid()) return parsed.format("DD MMM YYYY");
  }

  return String(raw);
};

// DetailItem
interface DetailItemProps {
  label: string;
  value?: string | number | null;
  placeholder?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({
  label,
  value = "",
  placeholder = "",
}) => {
  const stringValue = String(value ?? "");
  const isEmpty = stringValue.trim() === "";

  return (
    <div className="col-md-4 mb-3">
      <label className="form-label fw-semibold" title={label}>
        {label}
      </label>
      <input
        type="text"
        className="form-control"
        value={isEmpty ? "" : stringValue}
        placeholder={isEmpty ? placeholder || "—" : ""}
        disabled
        title={stringValue}
      />
    </div>
  );
};

const ManagerAddArtifact: React.FC = () => {
  const {
    case_id: caseId,
    assignment_id: assignId,
    assignment_template_id: assingTempId,
    check_id: check_id,
  } = useLocation()?.state || {};

  const [formData, setFormData] = useState<any>({});
  const [addressData, setAddressData] = useState<any[]>([]);

  const [uploadData, setUploadData] = useState(initialUploadData);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [docsInfo, setDocsInfo] = useState<any[]>([]);
  const [artifactStatus, setArtifactStatus] = useState<string>("");
  const [templateInfo, setTemplateInfo] = useState<any>(null);

  const [allItemList, setAllItemList] = useState<Artifact[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentItem[]>(
    [],
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [errors, setErrors] = useState<any>({});
  const [caseStatus, setCaseStatus] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  // API

  const { callApi: updateCaseStatusAPI, loading: loading_status_update } =
    useApi<any>(" ", {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    });

  const getCaseStatuses = useApi<any>(webservice_url.GetArtifactCaseStatus, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const { callApi: getCaseDetailsAPI, loading: loading_case } = useApi<any>(
    " ",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const { callApi: getArtifactsAPI, loading } = useApi<ArtifactsApiResponse>(
    "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const { callApi, loading: loading_upload } = useApi<any>(
    webservice_url.upload_artifacts,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const getTemplateDocument = useApi("", {
    autoCall: false,
    method: "GET",
    handleGlobalError: false,
  });

  useEffect(() => {
    const HandleGetCaseStatus = async () => {
      const res = await getCaseStatuses.callApi({ checkID: check_id });
      setCaseStatus(res?.data);
    };
    HandleGetCaseStatus();
  }, []);

  const getFileIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "ti-file-type-pdf";
    if (["jpg", "jpeg", "png"].includes(ext ?? "")) return "ti-photo";
    if (["msg", "eml"].includes(ext ?? "")) return "ti-mail";
    return "ti-file-text";
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatusId) {
      toast.error("Please select a status");
      return;
    }

    try {
      const res = await updateCaseStatusAPI(
        {
          status: selectedStatusId,
        },
        `${webservice_url.UpdateVerifierCaseStatus}/${assingTempId}`,
      );

      if (res?.success) {
        toast.success(res?.message || "Status updated successfully!");
        fetchData();
        fetchCheckData();
      }
    } catch (error: any) {
      console.error(error);
      const errMsg = error?.response?.data?.error || "Something went wrong";
      toast.error(errMsg);
    }
  };

  // ========== fetch Check data ===========

  const fetchCheckData = async () => {
    try {
      const res = await getCaseDetailsAPI(
        undefined,
        `${webservice_url.get_insufficiency_details}/${assingTempId}`,
      );
      if (res?.success) {
        const data = res?.data || {};
        setFormData(data);
        // Store response details except fields
        setTemplateInfo({
          response_id: data.response_id,
          assignment_template_id: data.assignment_template_id,
          is_draft: data.is_draft,
          submitted_at: data.submitted_at,
          updated_at: data.updated_at,
          check_name: data.check_name,
          form_type: data.form_type,
        });
        setAddressData(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err: any) {
      console.error(err?.response);
    }
  };
  useEffect(() => {
    fetchCheckData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getArtifactsAPI(
        undefined,
        `${webservice_url.get_all_artifacts}/${assingTempId}`,
      );

      if (res?.success) {
        const data: Artifact[] = Array.isArray(res?.data?.rows)
          ? res.data.rows
          : [];

        setAllItemList(data);

        // Status store karein
        setArtifactStatus(res?.data?.status || "");

        setPagination((prev) => ({
          ...prev,
          total: res?.pagination?.totalRecords ?? data.length,
        }));
      }
    } catch (err: any) {
      console.error(err?.response);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, caseId]);

  const handleViewDocuments = (documents: DocumentItem[]) => {
    setSelectedDocuments(documents || []);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDocuments([]);
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    }));
  };
  // ========== fetch data ===========

  // Fetch document
  const getTemplateDocs = async () => {
    try {
      const res = await getTemplateDocument.callApi(
        undefined,
        `/api/v1/insufficiency/documents/${assingTempId}`,
      );

      if (res?.success) {
        setDocsInfo(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!assingTempId) return;
    getTemplateDocs();
  }, [assingTempId]);

  // ========== Upload =========
  const handleFileChange: UploadProps["onChange"] = ({
    fileList: newFileList,
  }) => {
    setFileList(newFileList);

    setUploadData((prev: any) => ({
      ...prev,
      documents: newFileList.map((file) => file.originFileObj).filter(Boolean),
    }));

    handleClearError(setErrors, "documents");
  };

  const handleRemoveFile = (file: UploadFile) => {
    const updated = fileList.filter((f) => f.uid !== file.uid);
    handleFileChange({ file, fileList: updated });
  };

  const validateForm = () => {
    const newErrors: any = {};
    // if (!uploadData.remarks.trim()) {
    //   newErrors.remarks = "Remarks are required.";
    // }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   const isValid = validateForm();
  //   if (!isValid) return;

  //   try {
  //     const payload: any = {
  //       assignment_template_id: assingTempId || "",
  //       assignment_id: assignId || "",
  //       case_id: caseId || "",
  //       remarks: uploadData?.remarks ?? "",
  //       followup_date: uploadData?.followup_date ?? null,
  //       is_main_report: uploadData?.is_main_report ?? false,
  //       documents: fileList,
  //     };

  //     const formData = new FormData();

  //     Object.entries(payload).forEach(([key, value]) => {
  //       if (key === "documents") {
  //         (value as UploadFile[]).forEach((file) => {
  //           if (file.originFileObj) {
  //             formData.append("documents", file.originFileObj);
  //           }
  //         });
  //       } else {
  //         formData.append(key, String(value));
  //       }
  //     });

  //     const res = await callApi(formData);
  //     if (res?.success) {
  //       toast.success(res?.message || "Artifact added successfully!");
  //       setFileList([]);
  //       setUploadData(initialUploadData);
  //       fetchData();
  //     }
  //   } catch (error: any) {
  //     console.error(error);
  //     const errMsg = error?.response?.data?.error || "Something went wrong";
  //     toast.error(errMsg);
  //   }
  // };
  // ========== Upload =========

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasFollowupDate = Boolean(uploadData?.followup_date);
    const hasRemarks = Boolean(uploadData?.remarks?.trim());
    const hasDocuments = fileList.some((file) => file.originFileObj);
    // const hasMainReport = uploadData?.is_main_report === true;

    // At least one field must be provided
    if (!hasFollowupDate && !hasRemarks && !hasDocuments) {
      await Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please provide at least one detail: Follow-up Date, Remarks, Documents.",
        confirmButtonText: "OK",
      });
      return;
    }

    const isValid = validateForm();
    if (!isValid) return;

    try {
      const payload: any = {
        assignment_template_id: assingTempId || "",
        assignment_id: assignId || "",
        case_id: caseId || "",
        remarks: uploadData?.remarks ?? "",
        followup_date: uploadData?.followup_date ?? null,
        is_main_report: uploadData?.is_main_report ?? false,
        documents: fileList,
      };

      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "documents") {
          (value as UploadFile[]).forEach((file) => {
            if (file.originFileObj) {
              formData.append("documents", file.originFileObj);
            }
          });
        } else {
          formData.append(key, String(value));
        }
      });

      const res = await callApi(formData);

      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: res?.message || "Artifact added successfully!",
        });

        setFileList([]);
        setUploadData(initialUploadData);
        fetchData();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Failed",
          text: res?.message || "Unable to add artifact.",
        });
      }
    } catch (error: any) {
      console.error(error);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Something went wrong while adding the artifact.",
      });
    }
  };
  const getArtifactId = (record: Artifact) =>
    (record as any).id ?? (record as any).artifact_id;

  const { callApi: DeleteArtifact, loading: loading_delete } = useApi<any>("", {
    method: "DELETE",
    autoCall: false,
    handleGlobalError: false,
  });

  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const handleDelete = async (record: Artifact) => {
    // apni actual id field yahan lagao (id / artifact_id / _id ...)
    const artifactId = (record as any).id ?? (record as any).case_artifact_id;
    console.log("artifactId", artifactId);

    if (!artifactId) {
      toast.error("Artifact ID not found.");
      console.error("Missing id in record:", record);
      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This artifact will be permanently deleted.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      setDeletingId(artifactId);

      const res = await DeleteArtifact(
        undefined,
        `${webservice_url.DeleteArtifactCase}/${artifactId}`,
      );

      if (res?.success) {
        toast.success(res?.message || "Artifact deleted successfully");
        fetchData();
      } else {
        toast.error(res?.error || res?.message || "Unable to delete artifact");
      }
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.error || error?.error || "Something went wrong.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<Artifact> = [
    {
      title: "Case ID",
      dataIndex: "case_id",
      width: 120,
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      onCell: () => cellWidth(400),
      render: (remarks: string) =>
        remarks ? remarks : <span className="text-muted">-</span>,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "remarks"),
    },
    {
      title: "Follow-up Date",
      dataIndex: "followup_date",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.followup_date).getTime() -
        parseDate(b.followup_date).getTime(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime(),
    },
    {
      title: "Documents",
      width: 160,
      render: (_: unknown, record: Artifact) => {
        const count = record.documents?.length || 0;
        if (count === 0) {
          return <span className="text-muted">No documents</span>;
        }
        return (
          <button
            className="btn btn-sm btn-link p-0"
            onClick={() => handleViewDocuments(record.documents)}
          >
            View ({count})
          </button>
        );
      },
    },
    {
      title: "Is Main Report",
      dataIndex: "is_main_report",
      render: (is_main_report: boolean) =>
        is_main_report ? (
          <span className="badge bg-success">Yes</span>
        ) : (
          <span className="badge bg-danger">No</span>
        ),
      sorter: (a: any, b: any) =>
        Number(a.is_main_report) - Number(b.is_main_report),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_: unknown, record: Artifact) => {
        const artifactId = getArtifactId(record);
        const isDeleting = deletingId === artifactId;

        return (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(record)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              <>
                <i className="ti ti-trash me-1" />
                Delete
              </>
            )}
          </button>
        );
      },
    },
  ];

  const formatStatusName = (value: string) => value.replace(/_/g, " ");

  const formatStatus = (status: string) => {
    if (!status) return "-";

    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isAddressCheck =
    templateInfo?.check_name?.trim().toLowerCase() === "address verification";

  // Uploaded Documents card sirf tab dikhe jab data ho
  const hasDocuments = docsInfo?.length > 0;

  // Single (fields) + Multiple (entries) dono ke liye normalised entries
  const formEntries = useMemo(() => getFormEntries(formData), [formData]);
  const isMultipleEntries = formEntries.length > 1;

  // Set current status as default in the dropdown
  useEffect(() => {
    if (!artifactStatus || !Array.isArray(caseStatus) || !caseStatus.length)
      return;

    const normalize = (v: any) =>
      String(v ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const current: any = caseStatus.find(
      (item: any) =>
        normalize(item.status_name) === normalize(artifactStatus) ||
        String(item.check_status_id) === String(artifactStatus),
    );

    if (current) {
      setSelectedStatusId(String(current.check_status_id));
    }
  }, [artifactStatus, caseStatus]);

  return (
    <div className="page-wrapper">
      <div className="content px-2 px-md-4 pb-5">
        {loading_case || loading ? (
          <PageLoader />
        ) : (
          <>
            <PageBreadcrumb
              title="Add Artifacts"
              breadcrumbs={breadcrumbItem}
              showBackButton={true}
              backRoute={-1}
            />
            <div className="d-flex justify-content-end">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold text-dark">
                  <i className="ti ti-info-circle me-1 text-primary"></i>
                  Status:
                </span>

                <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill">
                  {formatStatus(artifactStatus)}
                </span>
              </div>
            </div>
            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h4 className="fw-bold mb-0">Artifacts History</h4>
                {!loading && (
                  <span className="badge bg-light text-dark border px-3 py-2">
                    {pagination.total}{" "}
                    {pagination.total === 1 ? "record" : "records"}
                  </span>
                )}
              </div>

              {/* table */}
              <div className="card-body p-0">
                {loading ? (
                  <div style={{ padding: "40px 0" }}>
                    <Loading />
                  </div>
                ) : (
                  <Datatable
                    rowKey="case_artifact_id"
                    columns={columns}
                    dataSource={allItemList}
                    fixedHeader
                    tableHeight={650}
                    hideSizeChanger={true}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                    }}
                    handleTableChange={handleTableChange}
                  />
                )}
              </div>
            </div>

            {/* case details */}
            <div className="card mt-3">
              <div className="card-header bg-white py-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-file-text text-warning fs-3"></i>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-0">{formData?.check_name}</h6>
                      <small className="text-muted">
                        Last Updated:{" "}
                        {dayjs(formData?.updated_at).format(
                          "DD MMM YYYY, hh:mm A",
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {!isAddressCheck ? (
                <div className="card-body">
                  {loading_case ? (
                    <Loading />
                  ) : (
                    <>
                      {/* Multiple entries: Status alag row me upar */}
                      {isMultipleEntries && (
                        <div className="row gx-3">
                          <DetailItem
                            label="Status"
                            value={formatStatus(artifactStatus)}
                          />
                        </div>
                      )}

                      {formEntries.length === 0 ? (
                        <div className="row gx-3">
                          <DetailItem
                            label="Status"
                            value={formatStatus(artifactStatus)}
                          />
                        </div>
                      ) : (
                        formEntries.map((entry, entryIdx) => {
                          const visibleFields = (entry.fields || [])
                            .filter((f: any) =>
                              VISIBLE_FIELD_TYPES.includes(f?.field_type),
                            )
                            .sort(
                              (a: any, b: any) =>
                                (a?.display_order ?? 0) -
                                (b?.display_order ?? 0),
                            );

                          return (
                            <div
                              key={entry.entry_index ?? entryIdx}
                              className={
                                isMultipleEntries &&
                                entryIdx !== formEntries.length - 1
                                  ? "mb-4"
                                  : ""
                              }
                            >
                              {/* Sub heading sirf tab jab multiple entries ho */}
                              {isMultipleEntries && (
                                <div
                                  className="px-3 py-2 mb-3 rounded"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#344054",
                                    background: "#F9FAFB",
                                    border: "1px solid #EAECF0",
                                  }}
                                >
                                  {formData?.check_name} ({entryIdx + 1} of{" "}
                                  {formEntries.length})
                                </div>
                              )}

                              <div className="row gx-3">
                                {/* Single entry: Status pehle ki tarah fields ke saath */}
                                {!isMultipleEntries && (
                                  <DetailItem
                                    label="Status"
                                    value={formatStatus(artifactStatus)}
                                  />
                                )}

                                {visibleFields.map((d: any) => (
                                  <DetailItem
                                    key={`${entry.entry_index}-${d?.field_id}`}
                                    label={d?.label}
                                    value={getFieldDisplayValue(d)}
                                    placeholder={d?.placeholder}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>
              ) : (
                <AddressDetailsView data={addressData} />
              )}
            </div>

            {/* Uploaded documents: sirf tab dikhe jab documents hon (Address check par hidden) */}
            {templateInfo?.check_name && !isAddressCheck && hasDocuments && (
              <div className="card mt-3">
                <div className="card-header bg-white py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-file-text text-warning fs-3"></i>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-0">Uploaded Documents</h6>
                      <small className="text-muted">
                        Candidate uploaded supporting documents
                      </small>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="row g-3">
                    {docsInfo.map((item) => (
                      <div
                        className="col-xl-3 col-lg-4 col-md-6"
                        key={item.file_id}
                      >
                        <div
                          className="card h-100 border-0 shadow-sm"
                          style={{
                            borderRadius: "14px",
                            transition: "0.25s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 20px rgba(0,0,0,0.12)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <div className="card-body text-center">
                            {/* Icon */}
                            <div
                              className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 70,
                                height: 70,
                                borderRadius: "50%",
                                background: "#FDECEC",
                              }}
                            >
                              <PdfIcon size={42} color="#D32F2F" />
                            </div>

                            {/* Document Name */}
                            <h6
                              className="fw-bold text-capitalize mb-2"
                              title={item.field_key?.replaceAll("_", " ")}
                            >
                              {item.field_key?.replaceAll("_", " ")}
                            </h6>

                            {/* File Name */}
                            <div className="small text-muted text-truncate mb-3">
                              {item.original_name}
                            </div>

                            {/* Info */}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                {item.mime_type?.split("/")[1]?.toUpperCase()}
                              </span>

                              <span className="badge bg-light text-dark border">
                                {new Date(
                                  item.uploaded_at,
                                ).toLocaleDateString()}
                              </span>
                            </div>

                            {/* View Button */}
                            <a
                              href={`${apibaseurl}/uploads/${item.s3_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary w-100 mt-2"
                            >
                              <i className="ti ti-eye me-1"></i>
                              View Document
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* upload artifact form */}
            <div className="card mt-3">
              <div className="card-header bg-white py-3 border-bottom">
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="ti ti-upload text-warning fs-3"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0">Upload Artifact Form</h6>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="card-body">
                <div className="d-md-flex gap-4">
                  <div className="w-100 w-md-50">
                    <div className="mb-3">
                      <label
                        htmlFor="followup_date"
                        className="form-label fw-semibold"
                      >
                        Select a Follow-up Date{" "}
                      </label>
                      <DatePicker
                        name="followup_date"
                        className="w-100 mb-1"
                        size="middle"
                        style={{ borderRadius: 8 }}
                        format={{ format: "DD-MM-YYYY", type: "mask" }}
                        placeholder="DD-MM-YYYY"
                        disabledDate={(current) =>
                          current && current.isBefore(dayjs(), "day")
                        }
                        value={
                          uploadData?.followup_date
                            ? dayjs(uploadData.followup_date, "YYYY-MM-DD")
                            : null
                        }
                        onChange={(d) => {
                          setUploadData((prev: any) => ({
                            ...prev,
                            followup_date: d ? d.format("YYYY-MM-DD") : null,
                          }));

                          handleClearError(setErrors, "followup_date");
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="remarks"
                        className="form-label fw-semibold"
                      >
                        {/* Remarks <span className="text-danger">*</span> */}
                        Remarks
                      </label>
                      <textarea
                        id="remarks"
                        className={`mb-1 form-control${errors.remark ? " is-invalid" : ""}`}
                        rows={4}
                        placeholder="Enter remark"
                        value={uploadData?.remarks ?? ""}
                        onChange={(e) => {
                          setUploadData((prev: any) => ({
                            ...prev,
                            remarks: e.target.value,
                          }));
                          handleClearError(setErrors, "remarks");
                        }}
                      />
                      {/* <ErrorMessage message={errors?.remarks} /> */}
                    </div>
                    <div
                      className="p-3 mb-3"
                      style={{
                        border: errors?.is_main_report
                          ? "1px solid #dc3545"
                          : "1px solid #D0D5DD",
                        borderRadius: 8,
                        background: "#F9FAFB",
                      }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <input
                          type="checkbox"
                          id="add_artifact"
                          className="form-check-input mt-1"
                          checked={uploadData?.is_main_report ?? false}
                          onChange={(e) => {
                            setUploadData((prev: any) => ({
                              ...prev,
                              is_main_report: e.target.checked,
                            }));
                            handleClearError(setErrors, "is_main_report");
                          }}
                        />

                        <label
                          htmlFor="add_artifact"
                          className="mb-0"
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          Add Artifact to Main Report
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: "#667085",
                              marginTop: 3,
                            }}
                          >
                            Check this box if you want to include this artifact
                            in the main report.
                          </div>
                        </label>
                      </div>
                      <ErrorMessage message={errors?.is_main_report} />
                    </div>
                  </div>

                  <div className="w-100 w-md-50 mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label fw-semibold mb-0">
                        Upload Documents
                      </label>
                      {fileList?.length > 0 && (
                        <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle rounded-pill px-3 py-1">
                          {fileList.length} file{fileList.length > 1 ? "s" : ""}{" "}
                          selected
                        </span>
                      )}
                    </div>

                    <div className="upload-dropzone border rounded-3 p-4 text-center mb-1">
                      <div className="upload-icon-circle mx-auto mb-3">
                        <i
                          className="ti ti-cloud-upload"
                          style={{ fontSize: 28 }}
                        ></i>
                      </div>

                      <h6 className="fw-semibold mb-1">
                        Upload Supporting Document
                      </h6>
                      <p className="text-muted small mb-3">
                        Click below to browse your files
                      </p>

                      <Upload
                        multiple
                        beforeUpload={() => false}
                        fileList={fileList}
                        onChange={handleFileChange}
                        showUploadList={false}
                        accept=".jpg,.jpeg,.png,.pdf,.msg,.eml"
                      >
                        <button type="button" className="upload-browse-btn">
                          <i className="ti ti-paperclip me-2"></i>
                          Choose Files
                        </button>
                      </Upload>

                      <p
                        className="text-muted mt-3 mb-0"
                        style={{ fontSize: 12 }}
                      >
                        Supports JPG, PNG, PDF — up to 10MB per file
                      </p>

                      {fileList?.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
                          {fileList.map((file) => (
                            <div
                              key={file.uid}
                              className="upload-file-chip d-flex align-items-center"
                            >
                              <i
                                className={`ti ${getFileIcon(file.name)} me-2`}
                              ></i>
                              <span
                                className="text-truncate"
                                style={{ maxWidth: 150 }}
                                title={file.name}
                              >
                                {file.name}
                              </span>
                              <button
                                type="button"
                                className="upload-remove-btn"
                                aria-label={`Remove ${file.name}`}
                                onClick={() => handleRemoveFile(file)}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <ErrorMessage message={errors?.documents} />
                  </div>
                </div>

                <div
                  className="d-flex justify-content-end mt-4 pt-3 gap-2"
                  style={{ borderTop: "1px solid #f5f5f5" }}
                >
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading_upload}
                  >
                    {loading_upload ? "Adding..." : "+ Add Artifacts"}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        <div className="card mt-3">
          <div className="card-header bg-white py-3 border-bottom">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                style={{ width: 40, height: 40 }}
              >
                <i className="ti ti-refresh text-warning fs-3"></i>
              </div>
              <div>
                <h6 className="fw-bold mb-0">Case Status</h6>
                <small className="text-muted">
                  Update the current status of this case
                </small>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="d-flex align-items-end gap-3 flex-wrap">
              <div style={{ minWidth: 500 }}>
                <label className="form-label fw-semibold">Select Status</label>
                <select
                  className="form-select"
                  value={selectedStatusId}
                  onChange={(e) => setSelectedStatusId(e.target.value)}
                >
                  <option value="">Select</option>
                  {caseStatus.map((item: any) => (
                    <option
                      key={item.check_status_id}
                      value={item.check_status_id}
                    >
                      {formatStatusName(item.status_name)}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="btn btn-primary"
                disabled={loading_status_update}
                onClick={handleUpdateStatus}
              >
                {loading_status_update ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>

        {/* view documents modal */}
        <Modal
          title={
            <span className="fw-bold">
              <i className="ti ti-folder-open me-2 text-primary"></i>
              Documents
            </span>
          }
          open={modalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={700}
        >
          {selectedDocuments.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="ti ti-folder-off text-secondary"
                style={{ fontSize: "48px" }}
              ></i>

              <h6 className="mt-3 mb-1">No Documents Found</h6>

              <p className="text-muted mb-0">No documents available</p>
            </div>
          ) : (
            <div className="row g-3">
              {selectedDocuments.map((doc) => (
                <div className="col-md-6" key={doc.document_id}>
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{
                      borderRadius: "14px",
                      transition: "0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div className="card-body">
                      <div className="d-flex">
                        {/* Icon */}
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
                          style={{
                            width: 55,
                            height: 55,
                            flexShrink: 0,
                          }}
                        >
                          <i className="ti ti-file-text text-danger fs-3"></i>
                        </div>

                        {/* Details */}
                        <div className="flex-grow-1">
                          <h6
                            className="fw-bold mb-1 text-truncate"
                            title={doc.original_name}
                          >
                            {doc.original_name}
                          </h6>

                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-muted small">Type:</span>
                            <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                              {doc.mime_type}
                            </span>
                          </div>

                          <div className="text-muted small mb-2">
                            Uploaded: {formattedDate(doc.uploaded_at)}
                          </div>
                          <a
                            href={`${apibaseurl}/uploads/${doc.file_name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary"
                          >
                            <i className="ti ti-eye me-1"></i>
                            View
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      </div>
      <Footer />
    </div>
  );
};

export default ManagerAddArtifact;
